

import React, { useState } from "react";
import SvgIcon, { SvgIconProps } from "@material-ui/core/SvgIcon";
import {
    fade,
    makeStyles,
    withStyles,
    Theme,
    createStyles,
    ThemeProvider,
} from "@material-ui/core/styles";
import TreeView from "@material-ui/lab/TreeView";
import TreeItem, { TreeItemProps } from "@material-ui/lab/TreeItem";
import Collapse from "@material-ui/core/Collapse";
import { useSpring, animated } from "@react-spring/web"; // web.cjs is required for IE 11 support
import { TransitionProps } from "@material-ui/core/transitions";
import theme from "js/material-ui-theme";
import { CategoriaProduto } from "@idha/idha-types/dist/orders/CategoriaProduto";

function MinusSquare(props: SvgIconProps) {
    return (
        <SvgIcon
            fontSize="inherit"
            style={{ width: 14, height: 14 }}
            {...props}
        >
            {/* tslint:disable-next-line: max-line-length */}
            <path d="M22.047 22.074v0 0-20.147 0h-20.12v0 20.147 0h20.12zM22.047 24h-20.12q-.803 0-1.365-.562t-.562-1.365v-20.147q0-.776.562-1.351t1.365-.575h20.147q.776 0 1.351.575t.575 1.351v20.147q0 .803-.575 1.365t-1.378.562v0zM17.873 11.023h-11.826q-.375 0-.669.281t-.294.682v0q0 .401.294 .682t.669.281h11.826q.375 0 .669-.281t.294-.682v0q0-.401-.294-.682t-.669-.281z" />
        </SvgIcon>
    );
}

function PlusSquare(props: SvgIconProps) {
    return (
        <SvgIcon
            fontSize="inherit"
            style={{ width: 14, height: 14 }}
            {...props}
        >
            {/* tslint:disable-next-line: max-line-length */}
            <path d="M22.047 22.074v0 0-20.147 0h-20.12v0 20.147 0h20.12zM22.047 24h-20.12q-.803 0-1.365-.562t-.562-1.365v-20.147q0-.776.562-1.351t1.365-.575h20.147q.776 0 1.351.575t.575 1.351v20.147q0 .803-.575 1.365t-1.378.562v0zM17.873 12.977h-4.923v4.896q0 .401-.281.682t-.682.281v0q-.375 0-.669-.281t-.294-.682v-4.896h-4.923q-.401 0-.682-.294t-.281-.669v0q0-.401.281-.682t.682-.281h4.923v-4.896q0-.401.294-.682t.669-.281v0q.401 0 .682.281t.281.682v4.896h4.923q.401 0 .682.281t.281.682v0q0 .375-.281.669t-.682.294z" />
        </SvgIcon>
    );
}

function CloseSquare(props: SvgIconProps) {
    return (
        <SvgIcon
            className="close"
            fontSize="inherit"
            style={{ width: 14, height: 14 }}
            {...props}
        >
            {/* tslint:disable-next-line: max-line-length */}
            <path d="M17.485 17.512q-.281.281-.682.281t-.696-.268l-4.12-4.147-4.12 4.147q-.294.268-.696.268t-.682-.281-.281-.682.294-.669l4.12-4.147-4.12-4.147q-.294-.268-.294-.669t.281-.682.682-.281.696 .268l4.12 4.147 4.12-4.147q.294-.268.696-.268t.682.281 .281.669-.294.682l-4.12 4.147 4.12 4.147q.294.268 .294.669t-.281.682zM22.047 22.074v0 0-20.147 0h-20.12v0 20.147 0h20.12zM22.047 24h-20.12q-.803 0-1.365-.562t-.562-1.365v-20.147q0-.776.562-1.351t1.365-.575h20.147q.776 0 1.351.575t.575 1.351v20.147q0 .803-.575 1.365t-1.378.562v0z" />
        </SvgIcon>
    );
}

function TransitionComponent(props: TransitionProps) {
    const style = useSpring({
        from: { opacity: 0, transform: "translate3d(20px,0,0)" },
        to: {
            opacity: props.in ? 1 : 0,
            transform: `translate3d(${props.in ? 0 : 20}px,0,0)`,
        },
    });

    return (
        <animated.div>
            <Collapse {...props} />
        </animated.div>
    );
}

const StyledTreeItem = withStyles((theme: Theme) =>
    createStyles({
        iconContainer: {
            "& .close": {
                opacity: 0.3,
            },
        },
        group: {
            marginLeft: 7,
            paddingLeft: 18,
            borderLeft: `1px dashed ${fade(theme.palette.text.primary, 0.4)}`,
        },
    })
)((props: TreeItemProps) => (
    <TreeItem {...props} TransitionComponent={TransitionComponent} />
));

export type CategoryTreeViewProps = {
    categorias: CategoriaProduto[];
    setCategoria: (value: CategoriaProduto) => void;
    enableAllNodesForSelection?: boolean;
};

export const CategoryTreeView = (props: CategoryTreeViewProps) => {
    const classes = useStyles();

    const firstLevel: CategoriaProduto[] = props.categorias.filter(
        (c) => !c.categoriaPaiId
    );

    const buildTree = (categoria: CategoriaProduto) => {
       
        const children = props.categorias.filter(
            (child) => child.categoriaPaiId == categoria.id
        );

        return (
            <>
                {children.length == 0 ? (
                    <StyledTreeItem
                        nodeId={categoria.name}
                        label={categoria.name}
                        onClick={() => props.setCategoria(categoria)}
                    />
                ) : (
                    <StyledTreeItem
                        nodeId={categoria.name}
                        label={categoria.name}
                        onClick={() =>
                            props.setCategoria(categoria)
                        }
                    >
                        {children.length > 0 &&
                            children.map((category) => buildTree(category))}
                    </StyledTreeItem>
                )}
            </>
        );
    };

    return (
        <ThemeProvider theme={theme}>
            <TreeView
                className={classes.root}
                defaultExpanded={["1"]}
                defaultCollapseIcon={<MinusSquare />}
                defaultExpandIcon={<PlusSquare />}
                defaultEndIcon={<CloseSquare />}
            >
                <StyledTreeItem nodeId="1" label="Categorias">
                    {firstLevel.map((category) => buildTree(category))}
                </StyledTreeItem>
            </TreeView>
        </ThemeProvider>
    );
};

const useStyles = makeStyles(
    createStyles({
        root: {
            height: 264,
            flexGrow: 1,
            maxWidth: 400,
        },
    })
);
